import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Button, Select } from '@artosera/ui';
import {
  formatDate,
  type EtatSignature,
  type SourceSignature,
  type DemandeSignaturePresentee,
} from '@artosera/shared';
import {
  useSignatures,
  useSignature,
  useGesteSignature,
  signaturesApi,
} from '../../api/signatures.js';
import { dansLaPorteeCourante } from '../../api/cles.js';
import { ouvrirUrlSignee } from '../../api/client.js';
import { galerieALaction } from '../../auth/sujet.js';
import { useShellContext } from '../shell/context.js';
import { SignatureFormulaire } from './SignatureFormulaire.js';
import styles from './Signatures.module.css';

/** Une date courte dans la langue de l'interface. */
function useDateCourte(): (iso: string) => string {
  const { i18n } = useTranslation();
  const langue = i18n.language === 'en' ? 'en' : 'fr';
  return (iso) => formatDate(new Date(iso), langue, 'court');
}

export function SignaturePiece({ source }: { source: SourceSignature }) {
  const { galerieSlug, galerie } = useShellContext();
  const { t } = useTranslation('regie');
  const date = useDateCourte();
  const [formulaire, setFormulaire] = useState(false);
  const liste = useSignatures(galerieSlug, { type: source.type, sourceId: source.id });
  const enCours = liste.data?.demandes.some(
    (d) => d.etat === 'envoyee' || (d.etat === 'signee' && !d.sourceModifiee),
  );
  return (
    <section className={styles.piece}>
      {liste.isPending && <p role="status">{t('signatures.chargement')}</p>}
      {liste.isError && <p role="alert">{t('signatures.echecLecture')}</p>}
      {liste.data?.demandes.map((d) => (
        <Link key={d.id} to={`/${galerieSlug}/signatures/${d.id}`}>
          {t(`signatures.etats.${d.etat}`)}
          {d.signeLe ? ` · ${date(d.signeLe)}` : ''}
        </Link>
      ))}
      {liste.data && !enCours && galerieALaction(galerie, 'signatures:write') && (
        <Button variant="secondary" onClick={() => setFormulaire(true)}>
          {t('signatures.envoyer')}
        </Button>
      )}
      {formulaire && (
        <SignatureFormulaire
          galerie={galerieSlug}
          source={source}
          fermer={() => setFormulaire(false)}
        />
      )}
    </section>
  );
}
export function SignaturesPage() {
  const { galerieSlug } = useShellContext();
  const { t } = useTranslation(['regie', 'commun']);
  const date = useDateCourte();
  const [etat, setEtat] = useState<EtatSignature>('envoyee');
  const [page, setPage] = useState(1);
  const liste = useSignatures(galerieSlug, { etat, page });
  return (
    <div data-ui="page" className={styles.page}>
      <h1>{t('regie:signatures.titre')}</h1>
      <Select
        aria-label={t('regie:signatures.etat')}
        value={etat}
        options={['envoyee', 'signee', 'refusee', 'expiree', 'annulee', 'brouillon'].map(
          (value) => ({ value, label: t(`regie:signatures.etats.${value}`) }),
        )}
        onValueChange={(v) => {
          setEtat(v as EtatSignature);
          setPage(1);
        }}
      />
      {liste.isPending && <p role="status">{t('commun:chargement')}</p>}
      {liste.isError && <p role="alert">{t('commun:erreurReseau')}</p>}
      {liste.data?.demandes.length === 0 && <p>{t('regie:signatures.aucune')}</p>}
      {liste.data?.demandes.map((d) => (
        <article key={d.id} className={styles.piece} data-ui="object-row">
          <Link to={`/${galerieSlug}/signatures/${d.id}`}>
            {t(`regie:signatures.sources.${d.source.type}`)} ·{' '}
            {d.signataires.map((s) => s.nom).join(', ')}
          </Link>
          <span>
            {t(`regie:signatures.etats.${d.etat}`)} · {date(d.expireLe)}
          </span>
        </article>
      ))}
      {liste.data && (
        <nav className={styles.actions}>
          <Button disabled={page === 1} onClick={() => setPage(page - 1)}>
            {t('regie:signatures.precedent')}
          </Button>
          <Button disabled={page * 50 >= liste.data.total} onClick={() => setPage(page + 1)}>
            {t('regie:signatures.suivant')}
          </Button>
        </nav>
      )}
    </div>
  );
}
export function SignatureDetailPage() {
  const { galerieSlug, galerie } = useShellContext();
  const { id = '' } = useParams();
  const { t } = useTranslation(['regie', 'commun']);
  const detail = useSignature(galerieSlug, id);
  if (detail.isPending) return <p role="status">{t('commun:chargement')}</p>;
  if (detail.isError) return <p role="alert">{t('commun:erreurReseau')}</p>;
  return (
    <Detail
      demande={detail.data.demande}
      galerieSlug={galerieSlug}
      peutEcrire={galerieALaction(galerie, 'signatures:write')}
    />
  );
}
function Detail({
  demande: d,
  galerieSlug,
  peutEcrire,
}: {
  demande: DemandeSignaturePresentee;
  galerieSlug: string;
  peutEcrire: boolean;
}) {
  const { t } = useTranslation(['regie', 'commun']);
  const label = (cle: string) => t(`regie:signatures.${cle}`);
  const geste = useGesteSignature(galerieSlug);
  const [confirmation, setConfirmation] = useState<'relancer' | 'annuler' | null>(null);
  const [erreur, setErreur] = useState('');
  const [chargement, setChargement] = useState(false);
  async function telecharger(nature: 'signe' | 'preuve') {
    setChargement(true);
    setErreur('');
    try {
      const r = await dansLaPorteeCourante((a) =>
        signaturesApi.fichier(galerieSlug, d.id, nature, a),
      );
      ouvrirUrlSignee(r.document.url);
    } catch {
      setErreur(t('commun:erreurReseau'));
    } finally {
      setChargement(false);
    }
  }
  return (
    <div className={styles.page} data-ui="page">
      <h1>{label('titre')}</h1>
      <p>
        {label(`etats.${d.etat}`)}
        {d.signeLe ? ` · ${new Date(d.signeLe).toLocaleString()}` : ''}
      </p>
      {d.sourceModifiee && <p role="status">{label('sourceModifiee')}</p>}
      {!d.envoiConfirme && <p role="alert">{label('envoiIncertain')}</p>}
      <p>
        {label('expiration')} : {new Date(d.expireLe).toLocaleString()}
      </p>
      <ol>
        {d.signataires.map((s) => (
          <li key={s.email}>
            {s.ordre}. {s.nom} · {s.email} · {s.signeLe ? label('signe') : label('enAttente')}
          </li>
        ))}
      </ol>
      {d.archivesDisponibles && (
        <div className={styles.actions}>
          <Button disabled={chargement} onClick={() => void telecharger('signe')}>
            {label('telechargerSigne')}
          </Button>
          <Button disabled={chargement} onClick={() => void telecharger('preuve')}>
            {label('telechargerPreuve')}
          </Button>
        </div>
      )}
      {peutEcrire && d.etat === 'envoyee' && (
        <div className={styles.actions}>
          <Button onClick={() => setConfirmation('relancer')}>{label('relancer')}</Button>
          <Button onClick={() => setConfirmation('annuler')}>{label('annuler')}</Button>
        </div>
      )}
      {confirmation && (
        <div className={styles.piece}>
          <p>{label('confirmation')}</p>
          <Button
            loading={geste.isPending}
            onClick={() => {
              void dansLaPorteeCourante((a) => signaturesApi.lire(galerieSlug, d.id, a))
                .then((r) =>
                  geste.mutateAsync({
                    id: d.id,
                    geste: confirmation,
                    revision: r.demande.revision,
                    cleIdempotence: crypto.randomUUID(),
                  }),
                )
                .then(() => setConfirmation(null))
                .catch(() => setErreur(label('conflit')));
            }}
          >
            {label('confirmer')}
          </Button>
          <Button onClick={() => setConfirmation(null)}>{t('commun:actions.fermer')}</Button>
        </div>
      )}
      {erreur && <p role="alert">{erreur}</p>}
    </div>
  );
}
